import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { toast } from "sonner";
import { X, UsersThree } from "@phosphor-icons/react";

export default function MyProjects() {
  const [projects, setProjects] = useState([]);
  const [teamFor, setTeamFor] = useState(null);
  const [teamData, setTeamData] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  const openTeam = async (p) => {
    setTeamFor(p); setTeamData(null);
    try { const r = await api.get(`/projects/${p.id}/team`); setTeamData(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="emp-projects">
      <PageHeader overline="Assigned" title="My Projects" subtitle="Projects you can log hours against — click to see the team." />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} onClick={() => openTeam(p)} className="bg-white border border-[#E5E5E5] p-5 cursor-pointer hover:border-[#1F4E8C] transition-colors duration-200" data-testid={`my-project-${p.project_id}`}>
            <div className="flex items-start justify-between">
              <div className="eyebrow">{p.project_id}</div>
              <StatusBadge status={p.status} />
            </div>
            <h3 className="font-head font-semibold text-xl mt-2 mb-1">{p.name}</h3>
            <div className="text-xs text-neutral-500 mb-4">{p.client}</div>
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#F0F0EE]">
              <div><div className="eyebrow text-[10px]">Budget</div><div className="font-head font-semibold">{p.budgeted_hours}h</div></div>
              <div><div className="eyebrow text-[10px]">Actual</div><div className="font-head font-semibold">{p.actual_hours}h</div></div>
              <div><div className="eyebrow text-[10px]">Util</div><div className="font-head font-semibold text-[#FF5500]">{p.utilization_pct}%</div></div>
            </div>
            <div className="text-[11px] text-neutral-500 mt-3 flex items-center gap-1">
              <UsersThree size={14} /> {(p.assigned_user_ids || []).length} members
            </div>
          </div>
        ))}
        {projects.length === 0 && <div className="text-neutral-500">No projects assigned yet.</div>}
      </div>

      {teamFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setTeamFor(null)}>
          <div className="bg-white border border-[#E5E5E5] max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-team-modal">
            <div className="p-6 border-b border-[#E5E5E5] bg-[#1F4E8C] text-white flex items-start justify-between">
              <div>
                <div className="eyebrow text-sky-200">{teamFor.project_id}</div>
                <h2 className="font-head text-2xl font-bold tracking-tight mt-1">{teamFor.name}</h2>
              </div>
              <button onClick={() => setTeamFor(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>
            {!teamData ? <div className="p-10 text-center text-neutral-500">Loading…</div> : (
              <>
                <div className="grid grid-cols-4 gap-3 p-6 bg-[#F7F7F5] border-b border-[#E5E5E5]">
                  <div><div className="eyebrow">Budget</div><div className="font-head text-2xl">{teamData.project.budgeted_hours}h</div></div>
                  <div><div className="eyebrow">Actual</div><div className="font-head text-2xl">{teamData.project.actual_hours}h</div></div>
                  <div><div className="eyebrow">Remaining</div><div className="font-head text-2xl">{teamData.project.remaining_hours}h</div></div>
                  <div><div className="eyebrow">Util</div><div className="font-head text-2xl text-[#FF5500]">{teamData.project.utilization_pct}%</div></div>
                </div>
                <div className="p-6">
                  <div className="eyebrow mb-3">Team · This Week ({teamData.week_from} → {teamData.week_to})</div>
                  <table className="w-full table-dense">
                    <thead><tr className="border-b-2 border-[#1F4E8C] text-left"><th className="eyebrow">Member</th><th className="eyebrow">This Week</th><th className="eyebrow">All Time</th></tr></thead>
                    <tbody>
                      {teamData.members.map((m) => (
                        <tr key={m.id} className="border-b border-[#F0F0EE]">
                          <td className="font-medium">{m.name}<div className="text-xs text-neutral-500">{m.department}</div></td>
                          <td className="font-head font-semibold text-[#1F4E8C]">{m.this_week_hours}h</td>
                          <td className="font-head font-semibold">{m.total_hours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
